import React, { useState, useEffect, useRef, useCallback } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community';
import '@ag-grid-community/styles/ag-theme-alpine.css';
import Papa from 'papaparse';
import './App.css';

ModuleRegistry.registerModules([AllCommunityModule]);

const DATE_FIELDS = ['Call Date', 'Maturity', 'Ex-Div', 'Issue Date'];
// Stored as plain numbers so the grid sorts them numerically; the % is added on display.
const PERCENT_FIELDS = ['YTC', 'YTM', 'CY', 'Coupon', 'Flt Sprd', 'PrjRtYld'];
const BOLD_FIELDS = ['Last', 'CY', 'YTC', 'YTM'];
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

const SUSPENDED_CLASS_RULE = {
  'cell-suspended': (params) =>
    typeof params.value === 'string' && params.value.toLowerCase().includes('suspend')
};

// Extracts local Y/M/D instead of toISOString() (which converts to UTC and can
// shift the date by a day for any viewer not in UTC).
const toLocalIsoDate = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

function App() {
  const [rowData, setRowData] = useState([]);
  const [columnDefs, setColumnDefs] = useState([]);
  const [currentFile, setCurrentFile] = useState('prefs2.csv'); // 'prefs2.csv', 'convertibles.csv', or 'all'
  const [selectedColKeys, setSelectedColKeys] = useState([]);
  const [underlyingFilter, setUnderlyingFilter] = useState([]);
  const allColumnDefsRef = useRef([]);
  const gridApiRef = useRef(null);
  const underlyingFilterRef = useRef([]);

  const numberFormatter = (params) => {
    if (params.value != null && typeof params.value === 'number') {
      return params.value.toFixed(2);
    }
    return params.value;
  };

  const percentFormatter = (params) => {
    if (params.value == null || params.value === '') return '';
    const numeric = typeof params.value === 'number' ? params.value : parseFloat(params.value);
    return Number.isFinite(numeric) ? `${numeric.toFixed(2)}%` : params.value;
  };

  const isDateField = (field) => DATE_FIELDS.includes(field);
  const isPercentField = (field) => PERCENT_FIELDS.includes(field);

  // Click an Underlying cell to show only rows for that issuer; Ctrl/Cmd-click adds
  // another issuer to the filter set instead of replacing it.
  const handleUnderlyingClick = useCallback((value, additive) => {
    setUnderlyingFilter((prev) => {
      if (additive) {
        return prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value];
      }
      return prev.length === 1 && prev[0] === value ? [] : [value];
    });
  }, []);

  // Stable reference (so it's set once per column and never needs re-creating) that
  // reads underlyingFilterRef at render time so the bold/active styling stays live.
  const UnderlyingCellRenderer = useCallback((params) => {
    const value = params.value;
    if (value == null || value === '') return null;
    const isActive = underlyingFilterRef.current.includes(value);
    return (
      <span
        onClick={(e) => handleUnderlyingClick(value, e.ctrlKey || e.metaKey)}
        title="Click to filter by this issuer (Ctrl/Cmd-click to add another)"
        style={{
          cursor: 'pointer',
          color: isActive ? '#004a99' : '#0076e5',
          textDecoration: 'underline',
          fontWeight: isActive ? 700 : 400
        }}
      >
        {value}
      </span>
    );
  }, [handleUnderlyingClick]);

  const isExternalFilterPresent = useCallback(() => underlyingFilterRef.current.length > 0, []);
  const doesExternalFilterPass = useCallback((node) => {
    if (underlyingFilterRef.current.length === 0) return true;
    return underlyingFilterRef.current.includes(node.data?.Underlying);
  }, []);

  const normalizeDateValue = useCallback((value) => {
    if (value == null || value === '' || value === '—' || value === '–' || value === 'N/A') {
      return null;
    }

    // CSV dates already arrive as plain "YYYY-MM-DD" — pass through unchanged
    // rather than round-tripping through Date (avoids timezone drift entirely).
    if (typeof value === 'string' && ISO_DATE_RE.test(value.trim())) {
      return value.trim();
    }

    let parsed = value;
    if (value instanceof Date) {
      parsed = value;
    } else {
      const text = String(value).trim();
      if (!text) return null;
      parsed = new Date(`${text}T00:00:00`);
    }

    if (Number.isNaN(parsed.getTime())) {
      return value;
    }

    return toLocalIsoDate(parsed);
  }, []);

  const normalizeDateFields = useCallback((row) => {
    const normalized = { ...row };
    DATE_FIELDS.forEach((field) => {
      if (Object.prototype.hasOwnProperty.call(normalized, field)) {
        normalized[field] = normalizeDateValue(normalized[field]);
      }
    });
    return normalized;
  }, [normalizeDateValue]);

  // Helper to parse a single CSV string
  const parseCsv = (csvText) => {
    return new Promise((resolve) => {
      Papa.parse(csvText, {
        header: true,
        skipEmptyLines: true,
        dynamicTyping: true,
        complete: (results) => resolve(results)
      });
    });
  };

  const loadData = useCallback(async (viewType, isRefresh = false) => {
    const baseUrl = process.env.PUBLIC_URL;
    const bust = `?t=${Date.now()}`;

    try {
      let combinedData = [];
      let combinedFields = new Set();

      if (viewType === 'all') {
        // Fetch both files simultaneously
        const [res1, res2] = await Promise.all([
          fetch(`${baseUrl}/prefs2.csv${bust}`),
          fetch(`${baseUrl}/convertibles.csv${bust}`)
        ]);

        const text1 = await res1.text();
        const text2 = await res2.text();

        const data1 = await parseCsv(text1);
        const data2 = await parseCsv(text2);

        combinedData = [...data1.data, ...data2.data].map(normalizeDateFields);
        // Merge field names from both files
        if (data1.meta.fields) data1.meta.fields.forEach(f => combinedFields.add(f));
        if (data2.meta.fields) data2.meta.fields.forEach(f => combinedFields.add(f));
      } else {
        // Fetch single file
        const res = await fetch(`${baseUrl}/${viewType}${bust}`);
        const text = await res.text();
        const results = await parseCsv(text);
        combinedData = results.data.map(normalizeDateFields);
        if (results.meta.fields) results.meta.fields.forEach(f => combinedFields.add(f));
      }

      // Update Rows
      setRowData(combinedData);

      // Auto-refresh keeps existing column selection; only rebuild on user-initiated load
      if (isRefresh) return;

      // Create Column Definitions. Underlying leads (before Symbol) so issuer
      // navigation via the click-to-filter links stays reachable without scrolling.
      const fieldsArray = Array.from(combinedFields);
      const underlyingIndex = fieldsArray.indexOf('Underlying');
      if (underlyingIndex > 0) {
        fieldsArray.splice(underlyingIndex, 1);
        fieldsArray.unshift('Underlying');
      }
      const cols = fieldsArray.map(field => {
        const isDate = isDateField(field);
        const isPercent = isPercentField(field);

        return {
          field,
          headerName: field,
          sortable: true,
          filter: isDate ? 'agDateColumnFilter' : true,
          resizable: true,
          cellDataType: isDate ? 'dateString' : undefined,
          cellRenderer: field === 'Underlying' ? UnderlyingCellRenderer : undefined,
          cellClass: BOLD_FIELDS.includes(field) ? 'cell-bold' : undefined,
          cellClassRules: field === 'Dividend' ? SUSPENDED_CLASS_RULE : undefined,
          comparator: isDate
            ? (valueA, valueB) => {
                const a = valueA ? new Date(`${valueA}T00:00:00`).getTime() : Number.NEGATIVE_INFINITY;
                const b = valueB ? new Date(`${valueB}T00:00:00`).getTime() : Number.NEGATIVE_INFINITY;
                return a - b;
              }
            : undefined,
          valueFormatter: isDate
            ? (params) => (params.value ? new Date(`${params.value}T00:00:00`).toLocaleDateString('en-CA') : '')
            : (isPercent ? percentFormatter : numberFormatter),
          valueParser: isDate
            ? (params) => {
                const parsed = new Date(`${params.newValue}T00:00:00`);
                return Number.isNaN(parsed.getTime()) ? null : toLocalIsoDate(parsed);
              }
            : undefined
        };
      });

      allColumnDefsRef.current = cols;

      // Default to all columns selected
      const allFields = cols.map(c => c.field);
      setSelectedColKeys(allFields);
      setColumnDefs(cols);

    } catch (err) {
      console.error("Error loading data:", err);
    }
  }, [normalizeDateFields, UnderlyingCellRenderer]);

  useEffect(() => {
    loadData(currentFile);
    const intervalId = setInterval(() => {
      loadData(currentFile, true);
    }, 5 * 60 * 1000);
    return () => clearInterval(intervalId);
  }, [currentFile, loadData]);

  const autoSizeAllVisibleColumns = useCallback(() => {
    const api = gridApiRef.current;
    if (!api) return;

    try {
      const visibleColumns = api.getAllDisplayedColumns() || [];
      const columnIds = visibleColumns.map((col) => col.getColId());
      if (columnIds.length) {
        api.autoSizeColumns(columnIds, false);
      }
    } catch (err) {
      console.error('Grid auto-size failed:', err);
    }
  }, []);

  const handleGridReady = useCallback((params) => {
    gridApiRef.current = params.api;
  }, []);

  // Keep the ref in sync and tell the grid to re-evaluate rows + repaint the
  // Underlying column whenever the filter set changes.
  useEffect(() => {
    underlyingFilterRef.current = underlyingFilter;
    const api = gridApiRef.current;
    if (!api) return;
    api.onFilterChanged();
    api.refreshCells({ columns: ['Underlying'], force: true });
  }, [underlyingFilter]);

  const handleColToggle = (field) => {
    setSelectedColKeys(prev => 
      prev.includes(field) ? prev.filter(f => f !== field) : [...prev, field]
    );
  };

  const applyColumnSelection = () => {
    const filteredCols = allColumnDefsRef.current.filter(col => 
      selectedColKeys.includes(col.field)
    );
    setColumnDefs(filteredCols);
  };

  const getButtonStyle = (isActive) => ({
    backgroundColor: isActive ? '#e1f0fe' : '#f8f9fa',
    color: isActive ? '#004a99' : '#333',
    padding: '8px 16px',
    border: isActive ? '1px solid #0076e5' : '1px solid #ccc',
    borderRadius: '4px',
    cursor: 'pointer',
    marginRight: '8px',
    fontWeight: isActive ? '600' : 'normal',
    transition: 'all 0.2s ease-in-out'
  });

  return (
    <div style={{ padding: '20px', height: '100vh', boxSizing: 'border-box', display: 'flex', flexDirection: 'column' }}>
      <div className="button-container" style={{ marginBottom: '15px', flexShrink: 0 }}>
        <button 
          onClick={() => setCurrentFile('prefs2.csv')} 
          style={getButtonStyle(currentFile === 'prefs2.csv')}
        >
          Preferreds & BBs
        </button>
        <button 
          onClick={() => setCurrentFile('convertibles.csv')} 
          style={getButtonStyle(currentFile === 'convertibles.csv')}
        >
          Convertibles
        </button>
        {/* NEW ALL BUTTON */}
        <button 
          onClick={() => setCurrentFile('all')} 
          style={getButtonStyle(currentFile === 'all')}
        >
          All
        </button>
      </div>

      <div style={{ padding: '15px', background: '#ffffff', marginBottom: '15px', borderRadius: '8px', border: '1px solid #e2e2e2', flexShrink: 0 }}>
        <div style={{ marginBottom: '10px' }}>
          <h4 style={{ margin: '0 0 6px 0', fontSize: '14px', color: '#555' }}>Select Columns:</h4>
        </div>
        <div style={{ 
          display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px', maxHeight: '120px', 
          overflowY: 'auto', border: '1px solid #eee', padding: '10px', 
          background: '#fafafa', borderRadius: '4px' 
        }}>
          {allColumnDefsRef.current.map(col => (
            <label key={col.field} style={{ fontSize: '12px', display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <input 
                type="checkbox" style={{ marginRight: '6px' }}
                checked={selectedColKeys.includes(col.field)} 
                onChange={() => handleColToggle(col.field)}
              />
              {col.headerName}
            </label>
          ))}
          <div style={{ display: 'flex', alignItems: 'center', marginLeft: '8px' }}>
            <button
              type="button"
              onClick={() => {
                const allFields = allColumnDefsRef.current.map((col) => col.field);
                setSelectedColKeys((prev) =>
                  prev.length === allFields.length ? [] : allFields
                );
              }}
              style={{ border: '1px solid #0076e5', background: '#f8fbff', color: '#004a99', borderRadius: '4px', padding: '4px 8px', cursor: 'pointer', fontSize: '12px' }}
            >
              {selectedColKeys.length === allColumnDefsRef.current.length ? 'Clear All' : 'Select All'}
            </button>
          </div>
        </div>
        <button 
          onClick={applyColumnSelection} 
          style={{ ...getButtonStyle(true), marginTop: '12px', fontSize: '13px' }}
        >
          Apply Selected Columns
        </button>
      </div>

      <div className="status-bar" style={{
        padding: '10px', fontSize: '13px', color: '#004a99',
        background: '#f0f7ff', borderLeft: '4px solid #0076e5', marginBottom: '10px', flexShrink: 0
      }}>
        Now showing: <strong>
          {currentFile === 'all' ? 'Combined View ( + Convertibles)' : 
           currentFile === 'convertibles.csv' ? 'Convertibles' : 'Preferreds & BBs'}
        </strong>
        <span style={{ marginLeft: '15px', color: '#666' }}>({rowData.length} rows, {columnDefs.length} columns)</span>
        {underlyingFilter.length > 0 && (
          <span style={{ marginLeft: '15px' }}>
            Filtered by Underlying: <strong>{underlyingFilter.join(', ')}</strong>
            <button
              type="button"
              onClick={() => setUnderlyingFilter([])}
              title="Clear Underlying filter"
              style={{
                marginLeft: '8px', border: '1px solid #0076e5', background: '#fff',
                color: '#004a99', borderRadius: '4px', padding: '1px 7px',
                cursor: 'pointer', fontSize: '12px', lineHeight: '16px'
              }}
            >
              ✕
            </button>
          </span>
        )}
      </div>

      <div className="ag-theme-alpine grid-container">
        <AgGridReact
          onGridReady={handleGridReady}
          onFirstDataRendered={autoSizeAllVisibleColumns}
          rowData={rowData}
          columnDefs={columnDefs}
          isExternalFilterPresent={isExternalFilterPresent}
          doesExternalFilterPass={doesExternalFilterPass}
          defaultColDef={{ minWidth: 40, filter: true, sortable: true, resizable: true }}
          animateRows={false}
          // Needed so the one-time auto-size (below) can measure every column, not just
          // the ones inside the initial horizontal viewport. Row virtualization (the more
          // expensive kind, given 637 rows vs 29 columns) is unaffected by this.
          suppressColumnVirtualisation={true}
          rowHeight={25}
          headerHeight={25}
        />
      </div>
    </div>
  );
}

export default App;